import { menuAnatomy } from '@chakra-ui/anatomy';
import {
  ChakraTheme,
  ThemeComponentProps,
  createMultiStyleConfigHelpers,
} from '@chakra-ui/react';
import { Poppins, Roboto } from 'next/font/google';

const menuHelpers = createMultiStyleConfigHelpers(menuAnatomy.keys);

export const poppins = Poppins({
  subsets: ['latin'],
  weight: ['300', '400', '500', '600', '700'],
});

export const roboto = Roboto({
  subsets: ['latin'],
  weight: ['300', '400', '500', '700'],
});

export const theme = {
  colors: {
    primary: {
      50: 'blue.50',
      100: 'blue.100',
      200: 'blue.200',
      300: 'blue.300',
      400: 'blue.400',
      500: 'blue.500', // #1676CE - Primary brand color
      600: 'blue.600', // #128280 - Primary dark (hover states)
      700: 'blue.700',
      800: 'blue.800',
      900: 'blue.900',
    },
  },
  textStyles: {
    xlBody: { fontSize: 'lg', lineHeight: '26px' },
    body: { fontSize: 'md', lineHeight: '6' },
    compact: { fontSize: 'sm', lineHeight: '5' },
    detail: { fontSize: 'xs', lineHeight: '5' },
    xlBodyStrong: {
      fontSize: 'lg',
      lineHeight: '26px',
      fontWeight: 'semibold',
    },
    bodyStrong: { fontSize: 'md', lineHeight: '6', fontWeight: 'semibold' },
    compactStrong: { fontSize: 'sm', lineHeight: '5', fontWeight: 'semibold' },
    detailStrong: { fontSize: 'xs', lineHeight: '5', fontWeight: 'semibold' },
  },
  fonts: {
    // heading: `"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
    // body: `"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
    body: `'roboto', sans-serif`,
    heading: `'Poppins', sans-serif`,
    table: `'roboto', sans-serif`,
    buttonFont: `'Poppins', sans-serif`,
    mono: 'monospace',
  },
  styles: {
    global: {
      body: {
        // bg: 'gray.50', old one
        bg: 'bgPrimary.500',
        color: "textSecondary.500",
      },
    },
  },
  components: {
    Text: {
      baseStyle: {
        fontFamily: "Roboto, sans-serif",
        // fontSize: "md",
        // fontWeight: "400",
        // color: "textSecondary.500",
        // lineHeight: "1.4",
      },
      sizes: {
        lg: { fontSize: "18px" },
        md: { fontSize: "16px" },
        sm: { fontSize: "14px" },
        xs: { fontSize: "12px" },
        xs2: { fontSize: "10px" },
      },
    },
    Textarea: {
      variants: {
        outline: {
          backgroundColor: 'white',
        },
      },
    },
    Input: {
       baseStyle: {
        backgroundColor: 'white',
        _hover: {
          bg: 'secondary.500'
        },
      },
      variants: {
        outline: {
          field: {
            backgroundColor: 'white',
          },
        },
      },
    },
    Tooltip: {
      baseStyle: {
        borderRadius: 'base',
        _hover: {
          bg: 'secondary.500',
        },
      },
    },
    FormLabel: {
      baseStyle: {
        fontSize: 'sm',
        lineHeight: '5',
        fontWeight: '500',
      },
    },
    Link: {
      baseStyle: {
        fontWeight: '500',
        color: 'textPrimary.500',
      },
    },
    Heading: {
      baseStyle: {
        fontFamily: "Poppins",
        fontWeight: "semibold",
        color: 'textPrimary.500',
      },
      sizes: {
        lg: { fontSize: ["22px", "24px"] },
        md: { fontSize: ["17px", "20px"] },
        sm: { fontSize: ["16px", "17px"] },
        xs: { fontSize: ["14px", "15px"] },
        base: { fontSize: "12px" },
      },
      variants: {
        inherit: {
          color: 'inherit', // This will inherit from parent
        },
        h1: {
          fontSize: { base: "22px", sm: "24px" },
          fontWeight: "600",
        },
        h2: {
          fontSize: { base: "17px", sm: "20px" },
          fontWeight: "600",
        },
        h3: {
          fontSize: { base: "16px", sm: "17px" },
          fontWeight: "600",
        },
        h4: {
          fontSize: { base: "14px", sm: "15px" },
          fontWeight: "600",
        },
      }
    },
  Button: {
  baseStyle: {
    fontFamily: "buttonFont",
    fontWeight: "500",
    _hover: {
      textDecoration: "none",
    },
  },

  sizes: {
    lg: { fontSize: "18px"},
    md: { fontSize: "16px"},
    sm: { fontSize: "14px"},
  },

  variants: {
    outline: {
      bg: "white",
      border: "1px solid",
      borderColor: "gray.300",
    },

    ghost: {
      bg: "transparent",
      _hover: {
        bg: "gray.100",
      },
    },
     menu: {
      bg: "white",
      border: "1px solid",
      borderColor: "strokePrimary.500",
      color: "textSecondary.500",
      fontWeight: "500",
      _active: {
        bg: "white",
        color: "textSecondary.500",
      },
      _focus: {
        bg: "white",
        color: "textSecondary.500",
      },
      // Style the arrow icon
      '& > span:nth-last-child(1)': {
        color: "primary.500",
      },
    },
  },

  defaultProps: {
    size: "md",
    variant: "solid",
    colorScheme: "primary",
  },
},
    Radio: {
      variants: {
        attached: {
          label: {
            w: 'full',
            ml: 4,
          },
          container: {
            bg: 'white',
            padding: '4',
            flex: '1',
            ':first-of-type': {
              borderTopRadius: 'xl',
            },
            ':last-child': {
              borderBottomRadius: 'xl',
            },
          },
        },
        spaced: {
          label: {
            w: 'full',
            ml: 4,
          },
          container: {
            borderWidth: '1px',
            borderColor: 'gray.200',
            borderRadius: 'xl',
            bg: 'white',
            padding: '17px',
            flex: '1',
            _checked: {
              padding: '16px',
              borderWidth: '2px',
              borderColor: 'primary.500',
              _disabled: {
                borderColor: 'gray.200',
              },
            },
          },
        },
      },
    },
    Checkbox: {
      baseStyle: {
        control: {
          bg: 'white',
        },
      },
      variants: {
        attached: {
          label: {
            w: 'full',
            ml: 4,
          },
          container: {
            bg: 'white',
            padding: '4',
            flex: '1',
            ':first-of-type': {
              borderTopRadius: 'xl',
            },
            ':last-child': {
              borderBottomRadius: 'xl',
            },
          },
        },
        spaced: {
          label: {
            w: 'full',
            ml: 4,
          },
          container: {
            borderWidth: '1px',
            borderColor: 'gray.200',
            borderRadius: 'xl',
            bg: 'white',
            padding: '17px',
            flex: '1',
            _checked: {
              padding: '16px',
              borderWidth: '2px',
              borderColor: 'primary.500',
              _disabled: {
                borderColor: 'gray.200',
              },
            },
          },
        },
        circle: {
          control: {
            borderRadius: 'full',
            _checked: {
              bg: 'green.300',
              borderColor: 'green.300',
              _hover: {
                borderColor: 'green.300',
              },
            },
          },
        },
      },
    },
    Alert: {
      baseStyle: {
        container: {
          alignItems: 'flex-start',
        },
      },
      variants: {
        outline: (props: ThemeComponentProps<ChakraTheme>) => ({
          container: {
            bg: `${props.colorScheme}.50`,
            border: '1px',
            borderRadius: 'md',
            borderColor: `${props.colorScheme}.500`,
          },
          icon: { color: `${props.colorScheme}.500` },
        }),
        subtle: {
          container: { borderRadius: 'md' },
        },
        solid: {
          container: { borderRadius: 'md' },
        },
      },
      defaultProps: {
        variant: 'outline',
      },
    },
    Tabs: {
      baseStyle: {
        tab: {
        _hover: {
          bg: 'secondary.500',
        },
      },
        tabpanel: {
          p: 0,
        },
      },
      variants: {
        line: (props: ThemeComponentProps<ChakraTheme>) => ({
          tablist: {
            borderBottomColor: 'transparent',
          },
          tab: {
            py: 4,
            color: 'gray.600',
            _selected: {
              fontWeight: 'semibold',
              color: 'gray.800',
              borderColor: `${props.colorScheme}.500`,
            },
          },
        }),
      },
    },
    Table: {
       baseStyle: {
        th: {
          fontFamily: 'table',
          color: "textPrimary.500",
          fontWeight: '500',
          borderBottomWidth: '0.6px',
          borderTopWidth: '0px',
          borderColor: 'strokePrimary.500',
        },
        td: {
          fontFamily: 'table',
          color: "textSecondary.500",
          borderBottomWidth: '0.6px',
          fontWeight: '400',
          borderColor: 'strokePrimary.500',
          // '& > span, & > strong, & > p, & div > p, & div > span & div > a, & p > a, a': {
          //   fontSize: 'inherit',
          //   fontWeight: 400,
          // },

          // ✔ child badge inside td should be fontWeight 500
          '& .chakra-badge': {
            // fontWeight: 500,
            // fontSize: '12px',
          },
        },
      },
      sizes: {
        sm: {
          th: {
            px: 2,
            py: 2.5,
            '&:first-of-type': {
              pl: 4,
            },
            '&:last-of-type': {
              pr: 4,
            },
            verticalAlign: 'bottom',
          },
          td: {
            px: 2,
            py: 2.5,
            '&:first-of-type': {
              pl: 4,
            },
            '&:last-of-type': {
              pr: 4,
            },
          },
        },
      },
      variants: {
        simple: {
          tr: {
            ':last-child': {
              td: {
                borderBottomColor: 'transparent',
              },
            },
          },
        },
        standard: {
          thead: { bg: 'secondary.10' },
          th: {
            fontSize: 'sm',
            verticalAlign: 'bottom',
            textTransform: 'capitalize',
            letterSpacing: '0'
          },
          tr: {
            ':not(:last-child)': {
              borderBottom: 'solid',
              borderBottomColor: 'gray.100',
              borderBottomWidth: '1px',
            },

          // s
          // '& td:is(:first-of-type, :nth-of-type(2)) p, & td:is(:first-of-type, :nth-of-type(2)) div p': {
          //   fontWeight: 500,
          // },

          },
          td: {
            fontSize: 'sm',
            lineHeight: '5',
            // fontWeight: '400'
         
          },
        },
        ghost: {
          thead: {
            borderBottom: 'solid',
            borderBottomColor: 'gray.100',
            borderBottomWidth: '1px',
          },
          th: {
            fontSize: 'xs',
            py: 4,
            verticalAlign: 'bottom',
          },
          tr: {
            ':not(:last-child)': {
              borderBottom: 'solid',
              borderBottomColor: 'gray.100',
              borderBottomWidth: '1px',
            },
          },
          td: {
            fontSize: 'sm',
            lineHeight: '5',
          },
        },
      },
      defaultProps: {
        variant: 'standard',
      },
    },
    Menu: menuHelpers.defineMultiStyleConfig({
      baseStyle: {
        button: {
          color: 'textSecondary.500',
        },
        list: {
          p: 2,
          borderRadius: 'xl',
          color: 'textSecondary.500',
          borderColor: 'strokePrimary.500',
          zIndex:'10',
          overflowY: 'auto',
          overflowX: 'hidden',
          maxH:'85vh',
        },
        item: {
          borderRadius: 'xl',
          // textStyle: 'bodyStrong',
          borderColor: 'white',
          borderWidth: '1px',
          color: 'textSecondary.500',
          fontWeight: '500',
          py: 2,
          px: 4,
          // _hover: {
          //   bg: 'grey.200',
          // },
          // _focus: {
          //   bg: 'secondary.500',
          // },
          // _active: {
          //   bg: 'primary.500',
          // },
        },
        groupTitle: {
          color: 'textSecondary.500',
          fontSize: 'sm',
          fontWeight: '500',
          mx: 2,
          textTransform: 'uppercase',
        },
      },
      sizes: {
        xs: {
          item: {
            py: 0,
            px: 2,
            borderRadius: 'lg',
            fontSize: 'xs',
          },
        },
      },
    }),
    Accordion: {
      variants: {
        baguette: {
          container: {
            borderTopWidth: 0,
            _last: {
              borderBottomWidth: 0,
            },
          },
          button: {
            px: 0,
            py: 0,
            _hover: {
              bg: 'none',
            },
          },
          panel: {
            px: 0,
          },
        },
      },
    },
    Popover: {
      baseStyle: {
        popper: {
          /** Will always be in front of modal overlays */
          zIndex: 1500,
        },
      },
    },
  },
};
